import React, { useState } from 'react';
import {
  Container,
  Paper,
  Box,
  Typography,
  Snackbar,
  Alert,
  Grid,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { 
  Lock as LockIcon,
  Email as EmailIcon
} from '@mui/icons-material';
import { useLocalization } from '../../contexts/LocalizationContext';
import Logo from '../../components/common/Logo';
import LoginForm from './components/LoginForm';
import ForgotPasswordForm from './components/ForgotPasswordForm';

const Login: React.FC = () => {
  const { t } = useLocalization();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [forgotPassword, setForgotPassword] = useState(false);
  const [toast, setToast] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success'
  });


  const handleCloseToast = () => {
    setToast({ ...toast, open: false });
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={4} alignItems="center" justifyContent="center">
          {/* Left Side - Welcome Content */}
          {!isMobile && (
            <Grid item xs={12} md={6}>
              <Box sx={{ color: 'white', textAlign: 'center', mb: 4 }}>
                <Box sx={{ mb: 3 }}>
                  <Logo size="large" variant="full" white={true} />
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 'bold',
                    mb: 2,
                    textShadow: '2px 2px 4px rgba(0,0,0,0.3)'
                  }}
                >
                  Welcome to SwapStop
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    opacity: 0.9,
                    mb: 3,
                    textShadow: '1px 1px 2px rgba(0,0,0,0.3)'
                  }}
                >
                  Secure locker management system for seamless transactions
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <LockIcon sx={{ fontSize: 40, mb: 1 }} />
                    <Typography variant="body2">Secure Lockers</Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center' }}>
                    <EmailIcon sx={{ fontSize: 40, mb: 1 }} />
                    <Typography variant="body2">Easy Management</Typography>
                  </Box>
                </Box>
              </Box>
            </Grid>
          )}

          {/* Right Side - Login Form */}
          <Grid item xs={12} md={6}>
            <Paper
              elevation={8}
              sx={{
                p: { xs: 3, md: 5 },
                borderRadius: 3,
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                maxWidth: 450,
                mx: 'auto',
                
              }}
            >
              {/* Mobile Logo */}
              {isMobile && (
                <Box sx={{ textAlign: 'center', mb: 3 }}>
                  <Logo size="small" variant="full" white={false} />
                </Box>
              )}

              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Box
                  sx={{
                    width: 60,
                    height: 60,
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 2
                  }}
                >
                  <LockIcon sx={{ color: 'white', fontSize: 30 }} />
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 'bold',
                    color: 'primary.main',
                    mb: 1
                  }}
                >
                  {!forgotPassword ? t.auth.login.title : t.auth.forgotPassword.title}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                >
                  {!forgotPassword 
                    ? 'Sign in to your account to continue'
                    : ''
                  }
                </Typography>
              </Box>

              {!forgotPassword ? (
                <LoginForm onForgotPassword={() => setForgotPassword(true)} />
              ) : (
                <ForgotPasswordForm onBackToLogin={() => setForgotPassword(false)} />
              )}
            </Paper>
          </Grid>
        </Grid>
      </Container>

      <Snackbar
        open={toast.open}
        autoHideDuration={6000}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseToast} severity={toast.severity} sx={{ width: '100%' }}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Login; 